import { useState } from 'react';
import { getTotalCalculation } from '../features/invoice/invoice.slice';
import { transformDeductionToWorkingHour } from '../utils/deduction.utils';

interface UseFreelancerCalculationBreakdownProps {
  friendUserId: string;
  selectedFreelancersForInvoice?: any[];
  serviceTableDataByFreelancer: Record<string, any>;
  deductionsByFreelancer: Record<string, any[]>;
  pensionDataByFreelancer: Record<string, any>;
  holidayDataByFreelancer: Record<string, any>;
  copiedInvoiceData?: any;
  removedCopiedWorkingHours?: Set<number>;
  calculatedAmounts?: any;
  preferences: any;
  globalData: any;
  customerState?: any;
  watch: (field: string) => any;
  dispatch?: any;
  createdDate?: string;
}

export const useFreelancerCalculationBreakdown = ({
  friendUserId,
  selectedFreelancersForInvoice = [],
  serviceTableDataByFreelancer,
  deductionsByFreelancer,
  pensionDataByFreelancer,
  holidayDataByFreelancer,
  copiedInvoiceData,
  removedCopiedWorkingHours = new Set(),
  calculatedAmounts,
  preferences,
  globalData,
  customerState,
  watch,
  dispatch,
  createdDate
}: UseFreelancerCalculationBreakdownProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [calculationData, setCalculationData] = useState<any>(null);

  const fetchCalculationBreakdown = async () => {
    if (!dispatch) {
      console.error('❌ [Freelancer Calculation] Dispatch not provided');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Calculate total amounts from ALL freelancers (same as main calculation)
      let totalAmountFromWorkingHours = 0;
      const allWorkingHours: any[] = [];
      const invoiceFreelancerArr: any[] = [];
      
      // Determine if this is a collective invoice
      const isCollectiveInvoice = selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 0;
      
      // Get all freelancer IDs (including 'default' for single invoice)
      const freelancerIds = isCollectiveInvoice 
        ? selectedFreelancersForInvoice.map((f: any) => (f.userId || f.id).toString())
        : ['default'];
      
      // Process each freelancer (using for...of to support async/await)
      for (const freelancerId of freelancerIds) {
        const serviceData = serviceTableDataByFreelancer[freelancerId] || { workingHours: [], totalAmount: 0 };
        const deductions = deductionsByFreelancer[freelancerId] || [];
        
        // Get actual userId
        const actualUserId = freelancerId === 'default' 
          ? (watch('userId') || 0)
          : parseInt(freelancerId);
        
        // Add service working hours with userId - preserve each working hour's VAT
        const serviceWorkingHours = serviceData.workingHours.map((wh: any) => ({
          ...wh,
          userId: actualUserId,
          vat: wh.vat != null ? wh.vat : (preferences?.vat || 25)  // Preserve working hour's VAT, fallback to global
        }));
        allWorkingHours.push(...serviceWorkingHours);
        
        // Add copied working hours from copiedInvoiceData for this freelancer (non-service articles only)
        if (copiedInvoiceData?.workingHours) {
          const copiedWorkingHoursForFreelancer = copiedInvoiceData.workingHours.filter((wh: any, index: number) => {
            const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
            const isThisFreelancer = wh.userId?.toString() === actualUserId.toString();
            const isRemoved = removedCopiedWorkingHours.has(index);
            return !isServiceArticle && isThisFreelancer && !isRemoved;
          }).map((wh: any) => ({
            ...wh,
            vat: wh.vat != null ? wh.vat : (preferences?.vat || 25)  // Preserve copied working hour's VAT
          }));
          allWorkingHours.push(...copiedWorkingHoursForFreelancer);
        }
        
        // Add deduction working hours with userId (MUST be async)
        for (const deduction of deductions) {
          try {
            const workingHour = await transformDeductionToWorkingHour(
              deduction, 
              preferences?.vat || 25, 
              (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || 'SEK'))?.globalValue || '1').toString()
            );
            if (workingHour) {
              allWorkingHours.push({
                ...workingHour,
                userId: actualUserId
              });
            }
          } catch (error) {
            console.error('Error transforming deduction in freelancer calculation:', error, deduction);
          }
        }
        
        // Calculate freelancer amount from working hours
        const deductionAmounts = await Promise.all(
          deductions.map(async (d) => {
            const wh = await transformDeductionToWorkingHour(
              d, 
              preferences?.vat || 25, 
              (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || 'SEK'))?.globalValue || '1').toString()
            );
            return wh ? wh.amount : 0;
          })
        );
        const freelancerAmount = [...serviceWorkingHours, ...deductionAmounts].reduce((sum: number, wh: any) => {
          return sum + (typeof wh === 'number' ? wh : (wh?.amount || 0));
        }, 0);
        
        totalAmountFromWorkingHours += freelancerAmount;
        
        // Get pension and holiday data for this freelancer
        const pensionData = pensionDataByFreelancer[freelancerId] || pensionDataByFreelancer['default'] || {};
        const holidayData = holidayDataByFreelancer[freelancerId] || holidayDataByFreelancer['default'] || {};
        
        invoiceFreelancerArr.push({
          userId: actualUserId,
          freelancerId: actualUserId,
          amount: freelancerAmount,
          pension: pensionData?.pension || false,
          pensionAmount: pensionData?.pensionAmount || 0,
          holidaySavings: holidayData?.holidaySavings || false,
          holidaySavingsFee: holidayData?.holidaySavingsFee || 0,
        });
      }
      
      // Determine pension and holiday savings
      const pensionToSend = invoiceFreelancerArr.some(f => f.pension);
      const holidaySavingsToSend = invoiceFreelancerArr.some(f => f.holidaySavings);
      const pensionAmountToSend = invoiceFreelancerArr.reduce((sum, f) => sum + (f.pensionAmount || 0), 0);
      const holidaySavingsFeeToSend = invoiceFreelancerArr.reduce((sum, f) => sum + (f.holidaySavingsFee || 0), 0);
      
      // Get deduction type from preferences
      const deductionType = preferences?.deductionType || 'none';
      const isROT = deductionType === 'rot';
      const isRUT = deductionType === 'rut';
      
      // Determine multipleFreelancer
      const multipleFreelancer = invoiceFreelancerArr.length > 1;
      
      // Deduplicate workingHours before sending to API
      const uniqueWorkingHours = allWorkingHours.reduce((acc: any[], current: any) => {
        const isDuplicate = acc.some((item: any) => {
          // Priority 1: If id exists and is not 0, check by id
          if (current.id && current.id !== 0 && item.id && item.id !== 0) {
            return item.id === current.id;
          }
          
          // Priority 2: If timeSheetWorkingHourId exists and is not 0, check by timeSheetWorkingHourId
          if (current.timeSheetWorkingHourId && current.timeSheetWorkingHourId !== 0 && 
              item.timeSheetWorkingHourId && item.timeSheetWorkingHourId !== 0) {
            return item.timeSheetWorkingHourId === current.timeSheetWorkingHourId;
          }
          
          // Priority 3: Check by dateId (unique identifier generated for each working hour)
          if (current.dateId && current.dateId !== '' && item.dateId && item.dateId !== '') {
            return item.dateId === current.dateId;
          }
          
          // Priority 4: For new working hours without id/dateId, check by key fields
          if ((!current.id || current.id === 0) && 
              (!current.timeSheetWorkingHourId || current.timeSheetWorkingHourId === 0) &&
              (!current.dateId || current.dateId === '')) {
            return item.workingDate === current.workingDate &&
                   item.workingHour === current.workingHour &&
                   item.amount === current.amount &&
                   item.articleNumber === current.articleNumber &&
                   item.userId === current.userId;
          }
          
          return false;
        });
        
        if (!isDuplicate) {
          acc.push(current);
        } else {
          console.log(`⚠️ Skipping duplicate working hour in freelancer calculation:`, current);
        }
        
        return acc;
      }, []);
      
      console.log(`🔍 Freelancer Calculation: Working hours deduplication ${allWorkingHours.length} → ${uniqueWorkingHours.length}`);
      
      // Build the exact same payload as main calculation
      const payload: any = {
        approvalInvoiceId: 0,
        rootDeductionAmount: calculatedAmounts?.rootDeductionAmount || 0,
        rutDeductionAmount: calculatedAmounts?.rutDeductionAmount || 0,
        invoiceId: 0,
        sendInvoice: 1,
        vat: preferences?.vat || 25,
        pension: pensionToSend,
        amount: totalAmountFromWorkingHours || 0,
        isFixed: watch('isFixed') || false,
        currency: preferences?.currency || 'SEK',
        brfNo: preferences?.brfOrgNumber || '',
        registerAssignment: false,
        showDates: watch('showDates') === 'yes' || watch('showDates') === true,
        house: preferences?.accommodationType === 'house',
        holidaySavings: holidaySavingsToSend,
        description: watch('otherDetails')?.customerNotes || watch('description') || '',
        taxValue: calculatedAmounts?.tax || 0,
        totalToPay: calculatedAmounts?.net || 0,
        rutDeduction: isRUT,
        rootDeduction: isROT,
        file: null,
        expressPayout: watch('expressPayout') || false,
        workingHours: uniqueWorkingHours,
        email: customerState?.selectedCustomer?.email || '',
        apartment: preferences?.accommodationType === 'apartment',
        pensionFee: pensionToSend ? (calculatedAmounts?.pensionPercentage || 0) : 0,
        holidaySavingsFee: holidaySavingsToSend ? holidaySavingsFeeToSend : 0,
        tax: watch('incomeTax') || 0,
        rotApartmentNo: preferences?.apartmentHouseNo || '',
        pensionAmount: pensionAmountToSend,
        customerId: customerState?.selectedCustomer?.customerId || 0,
        expenditureForMaterialsAndToolsTaxFreeAmount: 0,
        holidaySavingsAmount: calculatedAmounts?.holidayPayAmount || 0,
        amountIncVat: Math.round(calculatedAmounts?.amountIncludingVat || 0),
        secondaryEmailAddress: customerState?.selectedCustomer?.secondaryEmailAddress || '',
        multipleFreelancer: multipleFreelancer,
        currencyRate: globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || 'SEK'))?.globalValue || '1',
        invoiceFreelancer: invoiceFreelancerArr,
        locale: 'en-us',
        createdDate: createdDate || new Date().toISOString()
      };
      
      console.log('🔍 [Freelancer Calculation] Sending request with ALL freelancers, will filter for:', friendUserId, payload);
      
      // Call the API with ALL freelancers data
      const result = await dispatch(getTotalCalculation(payload)).unwrap();
       
      console.log('✅ [Freelancer Calculation] Response received, freelancersData:', result?.response?.freelancersData);
      
      // Store the full calculation data (modal will filter by friendUserId)
      if (result) {
        setCalculationData(result);
      }
    } catch (error) {
      console.error('❌ [Freelancer Calculation] Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    calculationData,
    isLoading,
    fetchCalculationBreakdown
  };
};
